import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  getNotificationsService,
  getUnreadCountService,
  markNotificationAsReadService,
  markAllNotificationsAsReadService,
  deleteNotificationService,
  invalidateUnreadCountCache,
} from "../../services/notifications";

const BellIcon = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
    />
  </svg>
);

const TrashIcon = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
  </svg>
);

const NotificationIcon = ({ notification, type }) => {
  const notifType = (type || notification?.type || "").toUpperCase();
  const module = (notification?.module || "").toUpperCase();
  const imageUrl = notification?.image_url || "";

  if (
    notifType === "KPI" ||
    module === "KPI_MODULE" ||
    module === "KPI" ||
    imageUrl.includes("kpi")
  ) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 text-indigo-600">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
    );
  }

  switch (notifType) {
    case "BOOKING":
    case "RECURRING":
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 ">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z"
            />
          </svg>
        </div>
      );
    case "CANCELLATION":
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-50 text-rose-600">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      );
    case "ADMIN":
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-50 text-amber-600">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
            />
          </svg>
        </div>
      );
    case "BROADCAST":
    case "DIRECT":
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 10h8M8 14h5m-8 7 2.5-2.5a2 2 0 011.414-.586H19a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2v3z"
            />
          </svg>
        </div>
      );
    case "FEED":
    case "ANNOUNCEMENT":
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-50 text-violet-600">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
            />
          </svg>
        </div>
      );
    default:
      return (
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 text-indigo-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
            />
          </svg>
        </div>
      );
  }
};

const getNotificationType = (notification) =>
  notification?.type?.toUpperCase?.() || "";

const formatNotificationDate = (date) => {
  if (!date) return "";
  const notifMoment = moment(date);
  if (!notifMoment.isValid()) return "";

  const now = moment();
  if (notifMoment.isSame(now, "day")) {
    return notifMoment.fromNow();
  }

  const yesterday = moment().subtract(1, "day");
  if (notifMoment.isSame(yesterday, "day")) {
    return "Yesterday";
  }

  return notifMoment.format("D MMM YYYY");
};

const isKpiNotification = (notification) => {
  const type = getNotificationType(notification);
  const module = (notification?.module || "").toUpperCase();
  const imageUrl = notification?.image_url || "";
  return (
    type === "KPI" ||
    module === "KPI_MODULE" ||
    module === "KPI" ||
    imageUrl.includes("kpi")
  );
};

const getNotificationTargetFeedId = (notification) =>
  notification?.feed_id ||
  notification?.feedId ||
  notification?.feed?.id ||
  notification?.feed?._id ||
  notification?.data?.feed_id ||
  notification?.data?.feedId ||
  notification?.data?.id ||
  notification?.data?._id ||
  notification?.meta?.feed_id ||
  notification?.meta?.feedId ||
  notification?.meta?.id ||
  notification?.meta?._id ||
  null;

const getNotificationTargetPath = (notification) => {
  if (!notification) return null;

  const type = getNotificationType(notification);

  // 1. KPI Notifications
  if (isKpiNotification(notification)) {
    const status = (notification?.status || "").toUpperCase();
    const entityType = (notification?.entity_type || "").toUpperCase();
    const metadata = notification?.metadata || {};

    // Check for Recurring Work Definition/Rule vs Task Instance
    const recurringId =
      metadata.recurring_id ||
      metadata.recurring_work_id ||
      (entityType === "RECURRING" ||
      entityType === "RECURRING_WORK" ||
      entityType === "RECURRING_TASK_DEFINITION"
        ? notification.entity_id
        : null);

    if (
      entityType === "RECURRING" ||
      entityType === "RECURRING_WORK" ||
      entityType === "RECURRING_TASK_DEFINITION" ||
      status.includes("RECURRING_DEFINITION")
    ) {
      return recurringId ? `/kpi/recurring/${recurringId}` : "/kpi/recurring";
    }

    if (
      entityType === "ASSESSMENT" ||
      status.includes("ASSESSMENT") ||
      status.includes("REVIEW")
    ) {
      return "/kpi/assessments";
    }

    if (
      entityType === "PROJECT" ||
      entityType === "GROUP" ||
      status.includes("PROJECT") ||
      status.includes("GROUP")
    ) {
      return "/kpi/groups";
    }

    if (entityType === "LEADERBOARD" || status.includes("LEADERBOARD")) {
      return "/kpi/leaderboard";
    }

    if (entityType === "CALENDAR" || status.includes("CALENDAR")) {
      return "/kpi/calendar";
    }

    if (
      entityType === "ACTIVITY" ||
      entityType === "ACTIVITY_LOG" ||
      status.includes("ACTIVITY")
    ) {
      return "/kpi/activity-log";
    }

    if (entityType === "SETTINGS" || status.includes("SETTINGS")) {
      return "/kpi/settings";
    }

    // Default KPI Task/Work redirection (e.g. TASK_OVERDUE, TASK_ASSIGNED, etc.)
    const taskId =
      metadata.task_id ||
      metadata.task_id?._id ||
      (entityType === "TASK" || entityType === "WORK" ? notification.entity_id : null) ||
      (status.includes("TASK") || status.includes("WORK") ? notification.entity_id : null) ||
      metadata.id ||
      metadata._id;

    if (taskId) {
      return `/kpi/works/${taskId}`;
    }

    if (
      entityType === "TASK" ||
      entityType === "WORK" ||
      status.includes("TASK") ||
      status.includes("WORK")
    ) {
      return notification.entity_id ? `/kpi/works/${notification.entity_id}` : "/kpi/works";
    }

    return notification.entity_id ? `/kpi/works/${notification.entity_id}` : "/kpi/dashboard";
  }

  // 2. Message / Non-KPI Notifications
  if (["BROADCAST", "DIRECT", "MESSAGE"].includes(type) || type.includes("MESSAGE")) {
    return "/";
  }

  if (["BOOKING", "RECURRING", "CANCELLATION"].includes(type)) {
    return "/bookings";
  }

  const feedId = getNotificationTargetFeedId(notification);
  if (
    type.includes("FEED") ||
    ["ANNOUNCEMENT", "UPDATE", "POST"].includes(type) ||
    feedId
  ) {
    return feedId ? `/feeds/${feedId}` : "/feeds";
  }

  return "/";
};

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("unread"); // "unread" | "all"
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();

  const dropdownRef = useRef(null);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
    // Poll unread count every 60 seconds
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch full notifications list when dropdown is opened or activeTab changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, activeTab]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCountService();
      if (response && typeof response.count === "number") {
        setUnreadCount(response.count);
      }
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  };

  const fetchNotifications = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const readParam = activeTab === "unread" ? false : undefined;
      const response = await getNotificationsService(1, 30, readParam);
      if (response && response.success && Array.isArray(response.data)) {
        // Prevent duplicates using unique ID Map/Filter
        const unique = [];
        const seen = new Set();
        response.data.forEach((n) => {
          if (n && n._id && !seen.has(n._id)) {
            seen.add(n._id);
            unique.push(n);
          }
        });
        setNotifications(unique);
      } else {
        setHasError(true);
      }
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (notification) => {
    if (notification.read) return;

    // Optimistically update local state
    setNotifications((prev) =>
      prev.map((n) => (n._id === notification._id ? { ...n, read: true } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await markNotificationAsReadService(notification._id);
      invalidateUnreadCountCache();
      // If we are in the "unread" tab, we should filter it out since it's now read
      if (activeTab === "unread") {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notification._id),
        );
      }
    } catch (err) {
      // Revert if API fails
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, read: false } : n,
        ),
      );
      setUnreadCount((prev) => prev + 1);
      toast.error("Failed to mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    // Optimistically mark all as read
    const originalNotifications = [...notifications];
    const originalCount = unreadCount;

    if (activeTab === "unread") {
      setNotifications([]);
    } else {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
    setUnreadCount(0);

    try {
      await markAllNotificationsAsReadService();
      invalidateUnreadCountCache();
      toast.success("All notifications marked as read");
    } catch (err) {
      // Revert if API fails
      setNotifications(originalNotifications);
      setUnreadCount(originalCount);
      toast.error("Failed to mark all as read");
    }
  };

  const handleDelete = async (notification) => {
    // Optimistically remove from state
    const originalNotifications = [...notifications];
    const originalCount = unreadCount;

    setNotifications((prev) => prev.filter((n) => n._id !== notification._id));
    if (!notification.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    try {
      await deleteNotificationService(notification._id);
      invalidateUnreadCountCache();
      toast.success("Notification deleted");
    } catch (err) {
      // Revert if API fails
      setNotifications(originalNotifications);
      setUnreadCount(originalCount);
      toast.error("Failed to delete notification");
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-slate-600 transition-all rounded-full hover:bg-slate-50 focus:outline-none flex items-center justify-center"
        aria-label="View notifications"
      >
        <BellIcon className="w-7 h-7 md:w-7 md:h-7" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-black text-white ring-2 ring-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="fixed lg:absolute left-4 right-4 lg:left-auto md:left-auto md:right-44 lg:right-0 top-[70px] md:top-auto mt-4 md:w-96 rounded-2xl bg-white border border-slate-300 shadow-2xl z-[9999] overflow-hidden transform origin-top-right transition-all duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b  border-slate-300">
            <h3 className="text-sm font-bold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs font-semibold text-black focus:outline-none"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Tabs (bg black and white text when active) */}
          <div className="flex px-5 py-2.5 border-b border-slate-100 bg-slate-50/30 gap-2">
            <button
              onClick={() => setActiveTab("unread")}
              className={`flex-1 py-1.5 text-xs font-black rounded-lg transition-all ${
                activeTab === "unread"
                  ? "bg-black text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-1 py-1.5 text-xs font-black rounded-lg transition-all ${
                activeTab === "all"
                  ? "bg-black text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All
            </button>
          </div>

          {/* List Area */}
          <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-300/70 scrollbar-thin ">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-3">
                <div className="w-8 h-8 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin" />
                <p className="text-xs font-semibold text-slate-400">
                  Loading notifications...
                </p>
              </div>
            ) : hasError ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <svg
                  className="w-8 h-8 text-rose-400 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-sm font-bold text-slate-700">
                  Failed to load notifications
                </p>
                <button
                  onClick={fetchNotifications}
                  className="mt-3 px-4 py-1.5 text-xs font-bold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all focus:outline-none"
                >
                  Try Again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center ">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 text-slate-400 mb-3">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0110 21.008a3.745 3.745 0 01-3.297-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z"
                    />
                  </svg>
                </div>
                <p className="text-sm font-bold text-slate-700">
                  All caught up!
                </p>
                <p className="text-xs text-slate-400 mt-1 max-w-[200px]">
                  {activeTab === "unread"
                    ? "You don't have any unread notifications."
                    : "You don't have any notifications at the moment."}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => {
                    handleMarkAsRead(notification);
                    setIsOpen(false);
                    const targetPath = getNotificationTargetPath(notification) || "/";
                    navigate(targetPath);
                  }}
                  className={`group flex items-start gap-3.5 p-4 transition-all duration-200 cursor-pointer relative  ${
                    notification.read
                      ? "hover:bg-slate-50/50 bg-white "
                      : "bg-indigo-50/20 hover:bg-indigo-50/40 "
                  }`}
                >
                  <div className="shrink-0 flex ">
                    <NotificationIcon notification={notification} type={notification.type} />
                  </div>
                  <div className="flex-1 min-w-0 pr-6">
                    <h4
                      className={`flex items-center gap-3 text-xs text-slate-800 truncate ${
                        !notification.read ? "font-bold" : "font-semibold"
                      }`}
                    >
                      <span className="truncate">{notification.title}</span>

                      {!notification.read && (
                        <span className="w-1.5 h-1.5 mb-1 rounded-full bg-emerald-500 flex-shrink-0" />
                      )}
                    </h4>
                    <p className="text-xs text-slate-500 font-medium mt-0.5 leading-relaxed break-words">
                      {notification.message}
                    </p>
                    <span className="text-[10px] font-bold text-slate-400 mt-1.5 block">
                      {formatNotificationDate(notification.createdAt)}
                    </span>
                  </div>
                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification);
                    }}
                    className="absolute right-3 top-4 p-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all "
                    title="Delete notification"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
